// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: c24d75e6-5f8d-4bb7-b061-5605ef9517ce

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Chip, Button, Divider,
  Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
  Collapse, IconButton, LinearProgress, Alert, Tooltip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { PICKLISTS, allFieldsFor, FIELD_TYPES } from './schema';
import { printElementToPdf, exportCsv } from './exports';
import HelpBubble from './HelpBubble';
import { buildNameIndex, lookupName } from './nameIndex';
import store, { computeCompletion } from './store';
import { TabLabel } from './ui';

// ---------------------------------------------------------------------------
// Readiness — the two questions Optimum gets asked on every engagement.
//
//   Tab 1 "Cutover readiness"  — "can we migrate this Saturday?"
//   Tab 2 "What's missing"     — "who owes me what before we can answer that?"
//
// Deliberately NOT a black-box score. Every point is an explicit, named,
// pass/fail criterion the client can argue with, and every failed criterion is
// visible on the row. A readiness number nobody can audit is a number nobody
// believes in a steering committee.
// ---------------------------------------------------------------------------

// Optimum palette.
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const GREEN = '#2E7D32';
const AMBER = '#F5A623';
const RED = '#C62828';

const NOT_SCHEDULED = 'Not Scheduled';

const tagVal = (a, group) => (a.tags || []).find((t) => t.group === group)?.value;
const waveOf = (a) => (a.fields?.cmWave || '').trim() || NOT_SCHEDULED;

// ---------------------------------------------------------------------------
// Dependency ordering. "Not Scheduled" is the backlog, not a late wave, so it
// has to be read as UNKNOWN. An integration partner nobody has scheduled yet is
// a GAP in the plan; it is not evidence the plan is out of order. Comparing
// wave indexes blindly makes every unscheduled partner look like a violation —
// and early in an engagement almost everything is unscheduled, which would fail
// the dependency criterion on applications that have done nothing wrong.
//
//   'ordered'     → nothing to report (target migrates with or before the source)
//   'later'       → genuine ordering violation, both sides scheduled
//   'unscheduled' → one side has no wave yet: a data gap, reported separately
//
// Kept identical to the copy in RoadmapView.js — the two screens must agree.
// ---------------------------------------------------------------------------
const classifyDependency = (fromWave, toWave, waveIndex) => {
  if (fromWave === NOT_SCHEDULED || toWave === NOT_SCHEDULED) return 'unscheduled';
  return waveIndex[toWave] > waveIndex[fromWave] ? 'later' : 'ordered';
};

// A value "counts" only if it is really there — '' , null, undefined and an
// unticked boolean are all "not captured yet".
const filled = (v) => {
  if (v === undefined || v === null || v === false) return false;
  if (typeof v === 'string') return v.trim() !== '';
  return true;
};

// ---------------------------------------------------------------------------
// TAB 1 — the criteria. Each is pass/fail and carries the remediation hint that
// gets read out loud in the readiness call.
// ---------------------------------------------------------------------------
const CRITERIA = [
  {
    key: 'ownership',
    label: 'Named business owner and director',
    hint: 'Set Business Owner (or Primary Stakeholder) and Primary Stakeholder - Director.',
    test: (a) => (filled(a.fields?.applicationOwner) || filled(a.fields?.primaryStakeholder))
      && filled(a.fields?.primaryStakeholderDirector),
  },
  {
    key: 'drTier',
    label: 'DR tier set',
    hint: 'Tag the app with a DR Tier, or set the DR Tier field.',
    test: (a) => filled(tagVal(a, 'DR Tier')) || filled(a.fields?.drTier),
  },
  {
    key: 'rtoRpo',
    label: 'RTO and RPO set',
    hint: 'Capture RTO and RPO under Disaster Recovery & Business Continuity.',
    test: (a) => filled(a.fields?.rto) && filled(a.fields?.rpo),
  },
  {
    key: 'hosting',
    label: 'Current and target hosting set',
    hint: 'Set Hosting Type and a target (Desired Future Hosting or Current Hosting Location).',
    test: (a) => filled(a.fields?.hostingType)
      && (filled(a.fields?.targetHosting) || filled(a.fields?.locationType)),
  },
  {
    key: 'disposition',
    label: 'Disposition (6R) decided',
    hint: 'Set 6R Classification (Portfolio Strategy) or CM: Disposition.',
    test: (a) => filled(a.fields?.sixRClassification) || filled(a.fields?.cmDisposition),
  },
  {
    key: 'network',
    label: 'Network detail captured',
    hint: 'Record firewall rules / ports, or the must-whitelist IPs and URLs.',
    test: (a) => filled(a.fields?.firewallRules) || filled(a.fields?.mustWhitelist),
  },
  {
    key: 'vendor',
    label: 'Vendor cloud-readiness confirmed',
    hint: 'Tick CM: Vendor Readiness, or at minimum name the vendor.',
    test: (a) => filled(a.fields?.cmVendorReadiness) || filled(a.fields?.vendorName),
  },
  {
    key: 'deps',
    label: 'No dependency scheduled in a later wave',
    hint: 'An integration partner migrates after this app — co-schedule the pair or plan the interface.',
    // ctx.laterDeps is computed across the whole inventory, not the filtered set,
    // and holds ONLY pairs where both sides have a wave. A partner that is not
    // scheduled yet is a gap in the plan, so it must not fail this criterion —
    // it is reported separately as an unscheduled dependency.
    test: (a, ctx) => !(ctx.laterDeps.get(a.id) || []).length,
  },
];
const N_CRITERIA = CRITERIA.length;

// ---------------------------------------------------------------------------
// OBSERVATIONS — called out, never scored.
//
// Frank, 2026-08-20: "when scoring I don't want some things to count to a score
// but we should call out things like seeing a resume that they have a new job
// every year or breaks in the job history."
//
// That is exactly the right distinction and it is not the same as a criterion.
// A criterion is a REQUIREMENT: RTO is set or it is not, and not setting it
// genuinely blocks a cutover, so it belongs in the percentage. An observation
// is a PATTERN: it might be perfectly fine, it might be the most important
// thing on the page, and only a person looking at it can tell. Folding those
// into a number does two bad things at once -- it penalises records that may be
// entirely correct, and it buries the signal inside an average.
//
// So these never touch pct. They render beside it, and the screen says they do
// not count.
//
// Every one is computable from data the product already holds. Nothing here is
// a guess about the client's estate: each observation states a fact and leaves
// the judgment to the reader, which is the same rule non-negotiable 8 applies
// to fields.
const OBSERVATIONS = [
  {
    key: 'saasWithServers',
    label: 'Recorded as SaaS but has servers attached',
    severity: 'warn',
    why: 'One of the two is wrong, and which one changes the migration entirely.',
    detect: (a) => /saas/i.test(a.fields?.hostingType || '')
      && ((a.relations?.servers || []).length > 0 || (a.relations?.itComponents || []).length > 0),
  },
  {
    key: 'cutoverPast',
    label: 'Planned cutover date has passed',
    severity: 'warn',
    why: 'The plan has drifted from the calendar. Neither the date nor the status is necessarily wrong, but they disagree.',
    detect: (a) => {
      const d = a.fields?.cutoverDate;
      if (!d) return false;
      const status = (a.fields?.lifecycleStatus || '').toLowerCase();
      return String(d) < new Date().toISOString().slice(0, 10)
        && status !== 'active' && status !== 'decommissioned';
    },
  },
  {
    key: 'noDeployment',
    label: 'No deployment recorded',
    severity: 'info',
    why: 'The application exists but nothing says where it runs, so it cannot be scheduled or costed.',
    detect: (a, ctx) => !(ctx.deploymentsByApp.get(a.id) || []).length,
  },
  {
    key: 'ownerOverloaded',
    label: 'Owner carries an outsized share of the portfolio',
    severity: 'info',
    why: 'Not a fault, and often just how a small team works. It is worth knowing before a wave depends on one person being available.',
    detect: (a, ctx) => {
      const owner = ownerOf(a);
      if (owner === 'Unassigned') return false;
      return (ctx.byOwner.get(owner) || 0) >= ctx.overloadThreshold;
    },
  },
  {
    key: 'versionNoDates',
    label: 'Version recorded, no end-of-support date anywhere',
    severity: 'info',
    why: 'Obsolescence risk cannot be assessed for this one, so it will sit quietly outside every EOL report.',
    detect: (a) => filled(a.fields?.version || a.version)
      && !filled(a.fields?.eosDate) && !filled(a.fields?.eolDate),
  },
  {
    key: 'scheduledButEmpty',
    label: 'Scheduled into a wave while still mostly blank',
    severity: 'warn',
    why: 'A date was committed before the detail existed. This is the pattern most likely to surface as a surprise on the night.',
    detect: (a, ctx) => filled(waveOf(a)) && (ctx.pctOf(a) || 0) < 40,
  },
  {
    key: 'unscheduledDep',
    label: 'Depends on something with no wave yet',
    severity: 'warn',
    why: 'The ordering is unknown rather than wrong, which is why it is reported here instead of failing the dependency criterion.',
    detect: (a, ctx) => (ctx.unscheduledDeps.get(a.id) || []).length > 0,
  },
];

const SEVERITY_COLOR = { warn: AMBER, info: GREY };


const statusOf = (pct) => (pct >= 90 ? 'Ready' : pct >= 70 ? 'Nearly' : 'Not ready');
const STATUS_COLOR = { Ready: GREEN, Nearly: AMBER, 'Not ready': RED };

// ---------------------------------------------------------------------------
// TAB 2 — which fields we coach on. Mandatory fields come from the schema; the
// high-value list is the handful that always blocks a migration plan even
// though LeanIX never marked them mandatory.
// ---------------------------------------------------------------------------
const HIGH_VALUE_KEYS = [
  'applicationOwner', 'drTier', 'targetHosting', 'rto', 'rpo',
  'sixRClassification', 'firewallRules', 'cmWave', 'cmEffort', 'vendorName',
];

// field.team when the schema carries it; otherwise the section decides.
const teamForField = (f) => {
  if (f.team) return f.team;
  const s = f.section || '';
  if (/network/i.test(s)) return 'Network Team';
  if (/security|authentication|compliance/i.test(s)) return 'InfoSec Team';
  if (/database|technical architecture/i.test(s)) return 'DB & Ops';
  return 'App Team';
};

const ownerOf = (a) => (a.fields?.applicationOwner || a.fields?.primaryStakeholder
  || a.fields?.technicalOwner || '').trim() || 'Unassigned';

export default function ReadinessView({ tenantId, onOpen }) {
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState([]);
  const [tenantName, setTenantName] = useState('');

  const [deployments, setDeployments] = useState([]);

  useEffect(() => { store.listFactSheets(tenantId, { type: 'Application' }).then(setApps); }, [tenantId]);
  // Deployments are fetched only so an application with none can be CALLED OUT.
  // Kept as its own request rather than widening the one above, because the
  // scoring criteria read Applications and nothing else, and mixing the two
  // lists would put Deployment rows through tests written for Applications.
  useEffect(() => {
    store.listFactSheets(tenantId, { type: 'Deployment' }).then(setDeployments);
  }, [tenantId]);
  useEffect(() => {
    let alive = true;
    store.listTenants().then((ts) => {
      if (!alive) return;
      const t = (ts || []).find((x) => x.id === tenantId);
      setTenantName(tenantId === 'optimum' ? 'This portfolio' : (t?.name || tenantId));
    });
    return () => { alive = false; };
  }, [tenantId]);

  const open = (id) => { if (onOpen) onOpen(id); };

  // --- Wave ordering + cross-wave dependencies (computed once, shared) ------
  const waveOrder = useMemo(() => {
    const known = PICKLISTS.migrationWave.filter((w) => w !== NOT_SCHEDULED);
    const extra = [...new Set(apps.map(waveOf))]
      .filter((w) => w !== NOT_SCHEDULED && !known.includes(w))
      .sort();
    return [...known, ...extra, NOT_SCHEDULED];
  }, [apps]);

  const waveIndex = useMemo(() => {
    const m = {};
    waveOrder.forEach((w, i) => { m[w] = i; });
    return m;
  }, [waveOrder]);

  // Two maps, never merged. Only in-inventory applications can be compared at
  // all: an external endpoint (vendor SaaS, IdP) has no wave.
  //   laterDeps       appId -> [{ toName, toWave }] both sides scheduled, target
  //                   migrates AFTER this app. A genuine ordering violation.
  //   unscheduledDeps appId -> [{ toName, toWave }] one side has no wave yet.
  //                   A gap in the plan; the ordering is unknown, not wrong.
  const { laterDeps, unscheduledDeps } = useMemo(() => {
    // Tenant-scoped: in the master workspace `apps` spans every client, so a
    // bare-name key made one client's app "depend on" another client's — and
    // rendered it as a clickable chip into their fact sheet (:476, :492).
    const byName = buildNameIndex(apps);
    const map = new Map();
    const gapMap = new Map();
    apps.forEach((a) => {
      const fromWave = waveOf(a);
      (a.integrations || []).forEach((it) => {
        const target = lookupName(byName, a, it?.to);
        if (!target || target.id === a.id) return;
        const toWave = waveOf(target);
        const kind = classifyDependency(fromWave, toWave, waveIndex);
        if (kind === 'ordered') return;
        const into = kind === 'later' ? map : gapMap;
        if (!into.has(a.id)) into.set(a.id, []);
        into.get(a.id).push({ toId: target.id, toName: target.name, toWave });
      });
    });
    return { laterDeps: map, unscheduledDeps: gapMap };
  }, [apps, waveIndex]);

  // --- Score every app against the visible criteria ------------------------
  const scored = useMemo(() => {
    // Context the OBSERVATIONS need. Built once here rather than per app,
    // because two of them are about an app's place among the others and would
    // otherwise be O(n^2) across a 7,000-record inventory.
    const byOwner = new Map();
    apps.forEach((a) => {
      const o = ownerOf(a);
      if (o === 'Unassigned') return;
      byOwner.set(o, (byOwner.get(o) || 0) + 1);
    });
    // "Outsized" has to be relative to the portfolio, not a fixed number: ten
    // applications is a heavy load in a estate of thirty and unremarkable in
    // one of six hundred. Three times the average, floored at five so a tiny
    // inventory cannot flag someone for owning two things.
    const owners = [...byOwner.values()];
    const avgOwned = owners.length ? owners.reduce((x, y) => x + y, 0) / owners.length : 0;
    const overloadThreshold = Math.max(5, Math.ceil(avgOwned * 3));

    const deploymentsByApp = new Map();
    (deployments || []).forEach((r) => {
      ((r.relations || {}).application || []).forEach((rel) => {
        if (!rel || !rel.id) return;
        if (!deploymentsByApp.has(rel.id)) deploymentsByApp.set(rel.id, []);
        deploymentsByApp.get(rel.id).push(r);
      });
    });

    const pctCache = new Map();
    const ctx = {
      laterDeps,
      unscheduledDeps,
      byOwner,
      overloadThreshold,
      deploymentsByApp,
      pctOf: (a) => pctCache.get(a.id),
    };

    return apps.map((a) => {
      const results = CRITERIA.map((c) => ({ ...c, pass: !!c.test(a, ctx) }));
      const met = results.filter((r) => r.pass).length;
      const pct = Math.round((met / N_CRITERIA) * 100);
      // Cached before the observations run, since one of them reads it.
      pctCache.set(a.id, pct);
      // Observations are computed AFTER pct and deliberately do not feed it.
      const observations = OBSERVATIONS
        .filter((o) => { try { return !!o.detect(a, ctx); } catch (e) { return false; } })
        .map((o) => ({ key: o.key, label: o.label, severity: o.severity, why: o.why }));
      return {
        observations,
        app: a,
        id: a.id,
        name: a.name,
        wave: waveOf(a),
        disposition: a.fields?.sixRClassification || a.fields?.cmDisposition || '',
        results,
        failed: results.filter((r) => !r.pass),
        met,
        pct,
        status: statusOf(pct),
        deps: laterDeps.get(a.id) || [],
        unscheduledDeps: unscheduledDeps.get(a.id) || [],
      };
    });
  }, [apps, deployments, laterDeps, unscheduledDeps]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Readiness</Typography>
        <HelpBubble topic="readinessView" />
      </Stack>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label={<TabLabel text="Cutover readiness" topic="tabCutoverReadiness" />} sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label={<TabLabel text="What's missing" topic="tabWhatsMissing" />} sx={{ fontWeight: 700, textTransform: 'none' }} />
      </Tabs>

      {tab === 0 ? (
        <CutoverTab scored={scored} waveOrder={waveOrder} onOpenApp={open} />
      ) : (
        <MissingTab apps={apps} tenantName={tenantName} onOpenApp={open} />
      )}
    </Box>
  );
}

// ===========================================================================
// TAB 1 — Cutover readiness
// ===========================================================================
function CutoverTab({ scored, waveOrder, onOpenApp }) {
  const [wave, setWave] = useState('All');
  const [status, setStatus] = useState('All');
  const [sortKey, setSortKey] = useState('pct');
  const [sortDir, setSortDir] = useState('asc'); // worst first: that is the work list
  const [expanded, setExpanded] = useState({});

  // Roll-up is over the FULL inventory — a wave does not become ready because
  // somebody filtered the broken apps out of view.
  const rollup = useMemo(() => waveOrder.map((w) => {
    const items = scored.filter((s) => s.wave === w);
    return {
      wave: w,
      total: items.length,
      ready: items.filter((s) => s.status === 'Ready').length,
      nearly: items.filter((s) => s.status === 'Nearly').length,
    };
  }).filter((r) => r.total > 0), [scored, waveOrder]);

  const filtered = useMemo(() => scored.filter((s) => {
    if (wave !== 'All' && s.wave !== wave) return false;
    if (status !== 'All' && s.status !== status) return false;
    return true;
  }), [scored, wave, status]);

  const rows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const get = {
      name: (s) => s.name || '',
      wave: (s) => s.wave,
      disposition: (s) => s.disposition || 'zzz',
      pct: (s) => s.pct,
      status: (s) => s.pct,
    }[sortKey] || ((s) => s.pct);
    return [...filtered].sort((x, y) => {
      const a = get(x); const b = get(y);
      const c = typeof a === 'number' ? a - b : String(a).localeCompare(String(b));
      return (c || String(x.name).localeCompare(String(y.name))) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const sortBy = (key) => {
    if (key === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const totalReady = filtered.filter((s) => s.status === 'Ready').length;

  // Reported as two separate counts. An ordering violation is a finding about
  // the plan; an unscheduled partner is a hole in the data. Summing them would
  // let missing data masquerade as cutover risk.
  const depViolations = filtered.reduce((n, s) => n + s.deps.length, 0);
  const depUnscheduled = filtered.reduce((n, s) => n + s.unscheduledDeps.length, 0);

  const downloadCsv = () => {
    const columns = [
      { label: 'Application', value: (r) => r.name },
      { label: 'Wave', value: (r) => r.wave },
      { label: 'Disposition', value: (r) => r.disposition },
      { label: 'Criteria met', value: (r) => `${r.met} of ${N_CRITERIA}` },
      { label: 'Readiness %', value: (r) => r.pct },
      { label: 'Status', value: (r) => r.status },
      ...CRITERIA.map((c) => ({
        label: c.label,
        value: (r) => (r.results.find((x) => x.key === c.key)?.pass ? 'Pass' : 'Fail'),
      })),
      { label: 'Failed criteria', value: (r) => r.failed.map((f) => f.label).join('; ') },
      // Its own column, so a spreadsheet cannot mistake an observation for a
      // failed requirement the way one merged column would invite.
      { label: 'Noted (does not affect score)', value: (r) => r.observations.map((o) => o.label).join('; ') },
      { label: 'Dependencies genuinely out of order', value: (r) => r.deps.map((d) => `${d.toName} (${d.toWave})`).join('; ') },
      { label: 'Dependencies not yet scheduled', value: (r) => r.unscheduledDeps.map((d) => `${d.toName} (${d.toWave})`).join('; ') },
    ];
    exportCsv('cutover-readiness.csv', rows, columns);
  };

  const header = (key, label, align) => (
    <TableCell align={align} sx={{ fontWeight: 700, color: HEAD, fontSize: 12, whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sortKey === key} direction={sortKey === key ? sortDir : 'asc'} onClick={() => sortBy(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <TextField select size="small" label="Wave" value={wave} onChange={(e) => setWave(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="All">All waves</MenuItem>
          {waveOrder.map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="All">All statuses</MenuItem>
          {['Ready', 'Nearly', 'Not ready'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <Typography sx={{ fontSize: 13, color: GREY }}>
          <Box component="b" sx={{ color: HEAD }}>{totalReady}</Box> of <Box component="b" sx={{ color: HEAD }}>{filtered.length}</Box> applications
          in view meet all {N_CRITERIA} criteria
        </Typography>
        <Tooltip title="Two different things: an ordering violation is a finding about the wave plan; an unscheduled partner is a gap in the data, and does not fail the dependency criterion.">
          <Typography sx={{ fontSize: 13, color: GREY }}>
            Dependencies: <Box component="b" sx={{ color: HEAD }}>{depViolations}</Box> genuinely out of order ·{' '}
            <Box component="b" sx={{ color: HEAD }}>{depUnscheduled}</Box> not yet scheduled
          </Typography>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={downloadCsv}>CSV</Button>
        <Button size="small" startIcon={<PictureAsPdfIcon />} variant="outlined"
          onClick={() => printElementToPdf('readiness-print', 'Cutover Readiness')}>PDF</Button>
      </Stack>

      <div id="readiness-print">
        {/* --- Per-wave roll-up: the one line the steering committee wants --- */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1.5, mb: 2 }}>
          {rollup.map((r) => {
            const pct = r.total ? Math.round((r.ready / r.total) * 100) : 0;
            return (
              <Paper key={r.wave} sx={{ p: 1.25, borderTop: `3px solid ${r.wave === NOT_SCHEDULED ? '#B0BEC5' : BLUE}` }}>
                <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>
                  {r.wave} — {r.ready} of {r.total} apps ready
                </Typography>
                <LinearProgress variant="determinate" value={pct}
                  sx={{ mt: 0.75, height: 6, borderRadius: 3, bgcolor: '#eceff1',
                    '& .MuiLinearProgress-bar': { bgcolor: pct >= 90 ? GREEN : pct >= 70 ? AMBER : RED } }} />
                <Typography sx={{ mt: 0.5, fontSize: 11, color: GREY }}>
                  {pct}% ready · {r.nearly} nearly · {r.total - r.ready - r.nearly} not ready
                </Typography>
              </Paper>
            );
          })}
          {rollup.length === 0 && (
            <Alert severity="info">No applications in this workspace yet.</Alert>
          )}
        </Box>

        <Paper sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell sx={{ width: 36 }} />
                {header('name', 'Application')}
                {header('wave', 'Wave')}
                {header('disposition', 'Disposition')}
                {header('pct', 'Criteria met', 'right')}
                {header('status', 'Status')}
                <TableCell sx={{ fontWeight: 700, color: HEAD, fontSize: 12 }}>Failing</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const isOpen = !!expanded[r.id];
                return (
                  <React.Fragment key={r.id}>
                    <TableRow hover sx={{ '& td': { borderBottom: isOpen ? 'none' : undefined } }}>
                      <TableCell sx={{ p: 0.25 }}>
                        <IconButton size="small" onClick={() => setExpanded({ ...expanded, [r.id]: !isOpen })}>
                          {isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box component="span" onClick={() => onOpenApp(r.id)}
                          sx={{ fontSize: 13, fontWeight: 700, color: BLUE, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                          {r.name}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: GREY, whiteSpace: 'nowrap' }}>{r.wave}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: GREY, whiteSpace: 'nowrap' }}>{r.disposition || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        <Box component="b" sx={{ color: HEAD }}>{r.met}</Box> of {N_CRITERIA}
                        <span style={{ color: GREY }}> · {r.pct}%</span>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.status}
                          sx={{ height: 19, fontSize: 11, fontWeight: 700, color: '#fff', bgcolor: STATUS_COLOR[r.status] }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: GREY, maxWidth: 320 }}>
                        {r.failed.length === 0 ? 'All criteria met' : r.failed.map((f) => f.label).join(' · ')}
                        {/* Observations sit BESIDE the failed criteria, never
                            inside them, and the count says "noted" rather than
                            anything that reads like a deduction. A record can
                            be 100% and still carry three of these. */}
                        {!!r.observations.length && (
                          <Chip size="small" variant="outlined"
                            label={`${r.observations.length} noted`}
                            sx={{ ml: 0.75, height: 17, fontSize: 10,
                              borderColor: r.observations.some((o) => o.severity === 'warn') ? AMBER : GREY,
                              color: r.observations.some((o) => o.severity === 'warn') ? AMBER : GREY }} />
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <Box sx={{ py: 1.25, pl: 2 }}>
                            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 12, mb: 0.5 }}>
                              Readiness checklist
                            </Typography>
                            {!!r.observations.length && (
                              <Box sx={{ mb: 1.25 }}>
                                <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 12, mb: 0.25 }}>
                                  Worth calling out
                                  <Box component="span" sx={{ fontWeight: 400, color: GREY, ml: 0.75 }}>
                                    — none of these affect the {r.pct}% above
                                  </Box>
                                </Typography>
                                <Stack spacing={0.4}>
                                  {r.observations.map((o) => (
                                    <Stack key={o.key} direction="row" alignItems="flex-start" spacing={1}>
                                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', mt: '5px',
                                        bgcolor: SEVERITY_COLOR[o.severity], flexShrink: 0 }} />
                                      <Box>
                                        <Typography sx={{ fontSize: 12, color: HEAD, fontWeight: 600 }}>{o.label}</Typography>
                                        <Typography sx={{ fontSize: 11, color: GREY }}>{o.why}</Typography>
                                      </Box>
                                    </Stack>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                            <Stack spacing={0.4}>
                              {r.results.map((c) => (
                                <Stack key={c.key} direction="row" alignItems="flex-start" spacing={1}>
                                  {c.pass
                                    ? <CheckCircleIcon sx={{ fontSize: 16, color: GREEN, mt: '1px' }} />
                                    : <CancelIcon sx={{ fontSize: 16, color: RED, mt: '1px' }} />}
                                  <Typography sx={{ fontSize: 12, color: c.pass ? GREY : HEAD, fontWeight: c.pass ? 400 : 700, minWidth: 260 }}>
                                    {c.label}
                                  </Typography>
                                  {!c.pass && (
                                    <Typography sx={{ fontSize: 11, color: GREY }}>{c.hint}</Typography>
                                  )}
                                </Stack>
                              ))}
                            </Stack>
                            {r.deps.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: 11, color: GREY, mb: 0.25 }}>
                                  {r.deps.length} {r.deps.length === 1 ? 'dependency' : 'dependencies'} genuinely
                                  out of order — both applications are scheduled and this one migrates first:
                                </Typography>
                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                  {r.deps.map((d) => (
                                    <Chip key={d.toId + d.toWave} size="small" onClick={() => onOpenApp(d.toId)}
                                      label={`${d.toName} · ${d.toWave}`}
                                      sx={{ height: 19, fontSize: 11, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700, cursor: 'pointer' }} />
                                  ))}
                                </Stack>
                              </Box>
                            )}
                            {r.unscheduledDeps.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: 11, color: GREY, mb: 0.25 }}>
                                  {r.unscheduledDeps.length} {r.unscheduledDeps.length === 1 ? 'dependency' : 'dependencies'} not
                                  yet scheduled — the ordering is unknown, not wrong, so this does not fail the
                                  dependency criterion:
                                </Typography>
                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                  {r.unscheduledDeps.map((d) => (
                                    <Chip key={d.toId + d.toWave} size="small" onClick={() => onOpenApp(d.toId)}
                                      label={`${d.toName} · ${d.toWave}`}
                                      sx={{ height: 19, fontSize: 11, bgcolor: '#eceff1', color: GREY, fontWeight: 700, cursor: 'pointer' }} />
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ fontSize: 12, color: GREY, py: 2 }}>
                    No applications match this filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Typography sx={{ mt: 1.5, fontSize: 11, color: GREY }}>
          Scoring is explicit: {N_CRITERIA} pass/fail criteria, equally weighted. Ready = 90%+ · Nearly = 70–89% ·
          Not ready = under 70%. Expand any row to see exactly which criteria failed and what closes them.
        </Typography>
      </div>
    </Box>
  );
}

// ===========================================================================
// TAB 2 — What's missing (the completeness coach)
// ===========================================================================
function MissingTab({ apps, tenantName, onOpenApp }) {
  const [groupMode, setGroupMode] = useState('team');
  const [expanded, setExpanded] = useState({});

  // The coached field set: every mandatory Application field plus the handful
  // of high-value fields that always block a migration plan. Deduped by key —
  // allFieldsFor() repeats a few keys across sections (hostingType, vendorName).
  const coachedFields = useMemo(() => {
    const byKey = new Map();
    allFieldsFor('Application').forEach((f) => {
      if (!f.mandatory && !HIGH_VALUE_KEYS.includes(f.key)) return;
      const prev = byKey.get(f.key);
      if (!prev) {
        byKey.set(f.key, { ...f, mandatory: !!f.mandatory });
        return;
      }
      // Merge duplicates: mandatory anywhere = mandatory; prefer the section
      // that resolves to a specific team over the App Team default.
      prev.mandatory = prev.mandatory || !!f.mandatory;
      if (teamForField(prev) === 'App Team' && teamForField(f) !== 'App Team') {
        prev.section = f.section;
        prev.team = f.team;
      }
    });
    // 'name' is never blank (it is the fact-sheet title) — coaching on it is noise.
    return [...byKey.values()].filter((f) => f.key !== 'name');
  }, []);

  // --- Gap rows: one per (group, field) pair -------------------------------
  const { rows, currentPct, projectedPct, totalCells, missingCells } = useMemo(() => {
    const map = new Map();
    let missing = 0;
    apps.forEach((a) => {
      coachedFields.forEach((f) => {
        if (filled(a.fields?.[f.key])) return;
        missing += 1;
        const group = groupMode === 'team' ? teamForField(f) : ownerOf(a);
        const k = `${group}||${f.key}`;
        if (!map.has(k)) {
          map.set(k, {
            key: k, group, fieldKey: f.key, label: f.label, section: f.section,
            mandatory: !!f.mandatory, apps: [],
          });
        }
        map.get(k).apps.push(a);
      });
    });
    const list = [...map.values()].sort(
      (x, y) => (y.apps.length - x.apps.length) || x.label.localeCompare(y.label)
    );
    const cells = apps.length * coachedFields.length;
    const cur = cells ? Math.round(((cells - missing) / cells) * 100) : 0;
    // Honest projection: close the top 3 rows entirely and re-divide.
    const top3 = list.slice(0, 3).reduce((n, r) => n + r.apps.length, 0);
    const proj = cells ? Math.round(((cells - missing + top3) / cells) * 100) : 0;
    return { rows: list, currentPct: cur, projectedPct: proj, totalCells: cells, missingCells: missing };
  }, [apps, coachedFields, groupMode]);

  // --- The OTHER completeness measurement ----------------------------------
  // This tab scores the ~15 fields we coach on. store.computeCompletion() scores
  // EVERY non-relation, non-lifecycle Application field (blended with any
  // "% Complete - ..." percentages already captured), and its portfolio mean is
  // what the Dashboard shows as "Avg. Completion". They are two different
  // measurements over two different field sets and routinely differ by 30
  // points. Printing only one of them under the bare word "complete" is what
  // made the product contradict itself, so both appear here, each named for
  // what it actually counts. computeCompletion is used as-is, never re-derived.
  const portfolioAvgPct = useMemo(() => {
    if (!apps.length) return 0;
    return Math.round(apps.reduce((n, a) => n + computeCompletion(a), 0) / apps.length);
  }, [apps]);

  const portfolioFieldCount = useMemo(() => allFieldsFor('Application').filter(
    (f) => f.type !== FIELD_TYPES.RELATION && f.type !== FIELD_TYPES.LIFECYCLE
  ).length, []);

  // Group totals, biggest impact first.
  const byGroup = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.group, (m.get(r.group) || 0) + r.apps.length));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const downloadCsv = () => {
    exportCsv(`missing-fields-by-${groupMode}.csv`, rows, [
      { label: groupMode === 'team' ? 'Responsible Team' : 'Owner / Stakeholder', value: (r) => r.group },
      { label: 'Field', value: (r) => r.label },
      { label: 'Field key', value: (r) => r.fieldKey },
      { label: 'Section', value: (r) => r.section || '' },
      { label: 'Mandatory', value: (r) => (r.mandatory ? 'Yes' : 'High value') },
      { label: 'Apps missing', value: (r) => r.apps.length },
      { label: 'Applications', value: (r) => r.apps.map((a) => a.name).join('; ') },
    ]);
  };

  const top3Labels = rows.slice(0, 3).map((r) => r.label);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={groupMode}
          onChange={(e, v) => { if (v) { setGroupMode(v); setExpanded({}); } }}>
          <ToggleButton value="team" sx={{ textTransform: 'none', fontWeight: 700 }}>By responsible team</ToggleButton>
          <ToggleButton value="person" sx={{ textTransform: 'none', fontWeight: 700 }}>By person</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={downloadCsv}>
          CSV worklist
        </Button>
      </Stack>

      <Paper sx={{ p: 1.5, mb: 2, borderLeft: `4px solid ${BLUE}` }}>
        <Typography sx={{ fontSize: 12, color: HEAD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Coached-field capture — {tenantName || 'this workspace'}
        </Typography>
        <Typography sx={{ fontSize: 14, color: HEAD, mt: 0.25 }}>
          <b>{currentPct}% of the {coachedFields.length} fields we coach on are captured</b>
          {rows.length >= 3 && (
            <> ; closing the top 3 gaps would raise that to ~<b>{projectedPct}%</b>.</>
          )}
          {rows.length > 0 && rows.length < 3 && <> ; only {rows.length} gap type remains.</>}
          {rows.length === 0 && <> — every coached field is captured on every application.</>}
        </Typography>
        {top3Labels.length > 0 && (
          <Typography sx={{ fontSize: 12, color: GREY, mt: 0.5 }}>
            Top 3: {top3Labels.join(' · ')}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, color: GREY, mt: 0.5 }}>
          Measured across {apps.length} applications × {coachedFields.length} coached fields
          ({coachedFields.filter((f) => f.mandatory).length} mandatory, {coachedFields.length - coachedFields.filter((f) => f.mandatory).length} high-value)
          = {totalCells} data points, {missingCells} blank.
        </Typography>

        <Divider sx={{ my: 1.25 }} />

        {/* A DIFFERENT measurement, named as such. Never merged with the figure
            above and never both called "complete". */}
        <Typography sx={{ fontSize: 12, color: HEAD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Portfolio average completeness — a different measurement
        </Typography>
        <Typography sx={{ fontSize: 14, color: HEAD, mt: 0.25 }}>
          <b>{portfolioAvgPct}% is the portfolio average across all {portfolioFieldCount} Application fields</b>
          {' '}— the figure shown on the Dashboard as “Avg. Completion”.
        </Typography>
        <Typography sx={{ fontSize: 11, color: GREY, mt: 0.5 }}>
          It is the mean of every application’s own score over every non-relation, non-lifecycle field in the
          Application schema, blended with any “% Complete — …” percentages already captured. The two numbers
          count different field sets, so they are expected to differ: <b>{currentPct}%</b> answers “are the
          fields we coach on filled in?”, <b>{portfolioAvgPct}%</b> answers “how full is the whole record?”.
          Neither is “the” completeness of {tenantName || 'this workspace'} — always say which one you mean.
        </Typography>
      </Paper>

      {byGroup.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.75 }}>
          {byGroup.map(([g, n]) => (
            <Tooltip key={g} title={`${n} blank fields owed by ${g}`}>
              <Chip size="small" label={`${g} · ${n}`}
                sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: 'chipMuted.main', color: HEAD }} />
            </Tooltip>
          ))}
        </Stack>
      )}

      <Paper>
        <Stack divider={<Divider flexItem />}>
          {rows.map((r) => {
            const isOpen = !!expanded[r.key];
            return (
              <Box key={r.key}>
                <Stack direction="row" alignItems="center" spacing={1}
                  onClick={() => setExpanded({ ...expanded, [r.key]: !isOpen })}
                  sx={{ px: 1, py: 0.9, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                  {isOpen ? <KeyboardArrowDownIcon fontSize="small" sx={{ color: GREY }} />
                    : <KeyboardArrowRightIcon fontSize="small" sx={{ color: GREY }} />}
                  <Typography sx={{ fontSize: 13, color: HEAD }}>
                    <b>{r.apps.length}</b> {r.apps.length === 1 ? 'app is' : 'apps are'} missing{' '}
                    <Box component="b" sx={{ color: HEAD }}>{r.label}</Box>
                    <span style={{ color: GREY }}> — {r.group}</span>
                  </Typography>
                  {r.mandatory && (
                    <Chip size="small" label="Mandatory"
                      sx={{ height: 17, fontSize: 10, fontWeight: 700, bgcolor: '#FDECEA', color: RED }} />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 11, color: '#90A4AE' }}>{r.section}</Typography>
                </Stack>
                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ px: 4, pb: 1.25 }}>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      {r.apps.map((a) => (
                        <Chip key={a.id} size="small" label={a.name} onClick={() => onOpenApp(a.id)}
                          sx={{ height: 20, fontSize: 11, cursor: 'pointer', bgcolor: 'chipMuted.main', color: HEAD,
                            '&:hover': { bgcolor: '#dbe6f2' } }} />
                      ))}
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
          {rows.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Alert severity="success" sx={{ py: 0.25 }}>
                No blank mandatory or high-value fields in this workspace.
              </Alert>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
